import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { EndpointMessageInterface } from "../../../interfaces/EndpointMessage.interface";
import { subscribeEndpointEvents } from "../../../flatbuffers/api/endpointInterceptor";
import { bigIntReplacer } from "./endpointMessageUtils";

const MAX_MESSAGES = 500;

export type EndpointMode = "idle" | "recording" | "replaying";

export interface EndpointCaptureFile {
  version: 1;
  capturedAt: string; // ISO timestamp
  messages: EndpointMessageInterface[];
}

interface EndpointMessagesContextValue {
  messages: EndpointMessageInterface[];
  clearMessages: () => void;
  mode: EndpointMode;
  captureReady: boolean;
  startCapture: () => void;
  stopCapture: () => void;
  downloadCapture: () => void;
  importReplay: (file: File) => void;
  exitReplay: () => void;
}

const EndpointMessagesCtx = createContext<EndpointMessagesContextValue>({
  messages: [],
  clearMessages: () => {},
  mode: "idle",
  captureReady: false,
  startCapture: () => {},
  stopCapture: () => {},
  downloadCapture: () => {},
  importReplay: () => {},
  exitReplay: () => {},
});

export function useEndpointMessages(): EndpointMessagesContextValue {
  return useContext(EndpointMessagesCtx);
}

/**
 * Provider that subscribes to the endpoint interceptor and buffers
 * up to MAX_MESSAGES events in FIFO order. Only mount this provider
 * when the Terminal is in endpoint mode — the subscription is the
 * signal to flatBufferBaseQuery that it should emit events.
 *
 * Supports three modes:
 * - idle: live events displayed as they arrive
 * - recording: live events displayed AND accumulated in captureBufferRef
 * - replaying: imported messages displayed; live events tracked in liveMessagesRef but not displayed
 */
export function EndpointMessagesProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<EndpointMessageInterface[]>([]);
  const [mode, setMode] = useState<EndpointMode>("idle");
  const [captureReady, setCaptureReady] = useState(false);

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const modeRef = useRef(mode);
  const captureBufferRef = useRef<EndpointMessageInterface[]>([]);
  const liveMessagesRef = useRef<EndpointMessageInterface[]>([]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    const unsubscribe = subscribeEndpointEvents((event) => {
      // Always track live messages for restoration on exitReplay
      liveMessagesRef.current = [...liveMessagesRef.current, event];
      if (liveMessagesRef.current.length > MAX_MESSAGES) {
        liveMessagesRef.current = liveMessagesRef.current.slice(-MAX_MESSAGES);
      }

      // During recording, also accumulate in capture buffer
      if (modeRef.current === "recording") {
        if (captureBufferRef.current.length < MAX_MESSAGES) {
          captureBufferRef.current = [...captureBufferRef.current, event];
        }
      }

      // Only update displayed messages when not replaying
      if (modeRef.current !== "replaying") {
        setMessages((prev) => {
          const next = [...prev, event];
          if (next.length > MAX_MESSAGES) {
            return next.slice(next.length - MAX_MESSAGES);
          }
          return next;
        });
      }
    });
    return unsubscribe;
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    captureBufferRef.current = [];
    liveMessagesRef.current = [];
    setCaptureReady(false);
    setMode("idle");
  }, []);

  const startCapture = useCallback(() => {
    captureBufferRef.current = [];
    setCaptureReady(false);
    setMode("recording");
  }, []);

  const stopCapture = useCallback(() => {
    setMode("idle");
    setCaptureReady(captureBufferRef.current.length > 0);
  }, []);

  const downloadCapture = useCallback(() => {
    const now = new Date().toISOString();
    const payload: EndpointCaptureFile = {
      version: 1,
      capturedAt: now,
      messages: captureBufferRef.current,
    };
    const jsonString = JSON.stringify(payload, bigIntReplacer, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `endpoint_capture_${now.slice(0, 19).replace(/:/g, "-")}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, []);

  const importReplay = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = JSON.parse(e.target?.result as string);
        if (!parsed || typeof parsed.version !== "number" || !Array.isArray(parsed.messages)) {
          return;
        }
        // Save current live messages for restoration on exitReplay
        liveMessagesRef.current = [...messagesRef.current];
        setMessages(parsed.messages as EndpointMessageInterface[]);
        setMode("replaying");
        setCaptureReady(false);
      } catch {
        // Invalid JSON — silently ignore; UI stays unchanged to signal import failure
      }
    };
    reader.readAsText(file);
  }, []);

  const exitReplay = useCallback(() => {
    setMode("idle");
    setMessages(liveMessagesRef.current);
  }, []);

  const value = useMemo(
    () => ({
      messages,
      clearMessages,
      mode,
      captureReady,
      startCapture,
      stopCapture,
      downloadCapture,
      importReplay,
      exitReplay,
    }),
    [
      messages,
      clearMessages,
      mode,
      captureReady,
      startCapture,
      stopCapture,
      downloadCapture,
      importReplay,
      exitReplay,
    ],
  );

  return <EndpointMessagesCtx.Provider value={value}>{children}</EndpointMessagesCtx.Provider>;
}
